import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Trash2, MoreHorizontal } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { useProjects, ProjectTemplate } from '@/hooks/useProjects';
import { TemplateModal } from '@/components/crm/TemplateModal';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

export default function CRMProjectTemplates() {
  const { templates, loading, refetch } = useProjects();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<ProjectTemplate | null>(null);
  const { toast } = useToast();

  const handleEdit = (template: ProjectTemplate) => {
    setSelectedTemplate(template);
    setIsModalOpen(true);
  };

  const handleDuplicate = async (template: ProjectTemplate) => {
    try {
      const { error } = await supabase
        .from('project_templates')
        .insert({
          name: `${template.name} (Kopie)`,
          description: template.description,
          schema: template.schema
        });

      if (error) throw error;

      toast({
        title: 'Template gedupliceerd',
        description: 'De template is succesvol gedupliceerd.',
      });

      refetch();
    } catch (error: any) {
      console.error('Error duplicating template:', error);
      toast({
        title: 'Fout bij dupliceren',
        description: 'Er ging iets mis bij het dupliceren van de template.',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (templateId: string) => {
    try {
      const { error } = await supabase
        .from('project_templates')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', templateId);

      if (error) throw error;

      toast({
        title: 'Template verwijderd',
        description: 'De template is succesvol verwijderd.',
      });

      refetch();
    } catch (error: any) {
      console.error('Error deleting template:', error);
      toast({
        title: 'Fout bij verwijderen',
        description: 'Er ging iets mis bij het verwijderen van de template.',
        variant: 'destructive'
      });
    }
  };

  const createQuickstartTemplate = async () => {
    try {
      const quickstartSchema = {
        sections: [
          {
            title: "Projectoverzicht",
            fields: [
              { id: "project_manager", label: "Projectmanager", type: "text" },
              { id: "start_date", label: "Startdatum", type: "date" },
              { id: "expected_completion", label: "Verwachte oplevering", type: "date" }
            ]
          },
          {
            title: "Technische specificaties",
            fields: [
              { id: "capacity", label: "Capaciteit (kW)", type: "number" },
              { id: "technology", label: "Technologie", type: "select", options: ["Solar PV", "Battery Storage", "Wind", "Hydro"] },
              { id: "location_details", label: "Locatiedetails", type: "textarea" }
            ]
          }
        ]
      };

      const { error } = await supabase
        .from('project_templates')
        .insert({
          name: 'Quickstart Template',
          description: 'Een basis template om snel te starten met projecten',
          schema: quickstartSchema
        });

      if (error) throw error;

      toast({
        title: 'Quickstart template aangemaakt',
        description: 'De quickstart template is succesvol aangemaakt.',
      });

      refetch();
    } catch (error: any) {
      console.error('Error creating quickstart template:', error);
      toast({
        title: 'Fout bij aanmaken template',
        description: 'Er ging iets mis bij het aanmaken van de quickstart template.',
        variant: 'destructive'
      });
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-foreground">Project Templates</h1>
        <div className="animate-pulse space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-muted rounded-lg h-32"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8 animate-fade-in">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-foreground">Project Templates</h1>
        
        <div className="flex gap-2">
          {templates.length === 0 && (
            <Button onClick={createQuickstartTemplate} variant="outline">
              <Plus className="mr-2 h-4 w-4" />
              Quickstart Template
            </Button>
          )}
          <Button onClick={() => {
            setSelectedTemplate(null);
            setIsModalOpen(true);
          }}>
            <Plus className="mr-2 h-4 w-4" />
            Nieuwe Template
          </Button>
        </div>
      </div>

      {templates.length === 0 ? (
        <div className="text-center py-12">
          <div className="mx-auto max-w-md">
            <h3 className="text-lg font-medium text-foreground mb-2">Geen templates gevonden</h3>
            <p className="text-muted-foreground mb-4">
              Begin met het aanmaken van je eerste project template om projecten gestructureerd te beheren.
            </p>
            <div className="space-y-2">
              <Button onClick={createQuickstartTemplate} variant="outline" className="w-full">
                <Plus className="mr-2 h-4 w-4" />
                Maak Quickstart Template
              </Button>
              <Button onClick={() => {
                setSelectedTemplate(null);
                setIsModalOpen(true);
              }} className="w-full">
                <Plus className="mr-2 h-4 w-4" />
                Aangepaste Template
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template) => (
            <Card key={template.id} className="hover:shadow-md transition-all duration-200">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">{template.name}</CardTitle>
                    {template.description && (
                      <p className="text-sm text-muted-foreground mt-1">{template.description}</p>
                    )}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                     <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleEdit(template)}>
                        <Edit className="mr-2 h-4 w-4" />
                        Bewerken
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDuplicate(template)}>
                        <Plus className="mr-2 h-4 w-4" />
                        Dupliceren
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                            <Trash2 className="mr-2 h-4 w-4" />
                            Verwijderen
                          </DropdownMenuItem>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Template verwijderen</AlertDialogTitle>
                            <AlertDialogDescription>
                              Weet je zeker dat je deze template wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annuleren</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDelete(template.id)}>
                              Verwijderen
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {template.schema?.sections ? (
                    <div>
                      <Badge variant="outline" className="text-xs">
                        {template.schema.sections.length} sectie(s)
                      </Badge>
                      <p className="text-xs text-muted-foreground mt-1">
                        {template.schema.sections.reduce((total: number, section: any) => 
                          total + (section.fields?.length || 0), 0)} veld(en)
                      </p>
                    </div>
                  ) : (
                    <Badge variant="secondary" className="text-xs">Geen schema</Badge>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Aangemaakt: {new Date(template.created_at).toLocaleDateString()}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <TemplateModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedTemplate(null);
        }}
        template={selectedTemplate}
        onSave={refetch}
      />
    </div>
  );
}